import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';

import { useTRPC } from '#src/trpc.js';

import type { HilManufacturer } from '@cadstrom/backend/shared';

const filter = createFilterOptions<HilManufacturer>();

export type ManufacturerPickerProps = {
	value: string | { create: string } | undefined;
	onChange: (manufacturerId: string | { create: string }) => void;

	disabled?: boolean;
};

export function ManufacturerPicker({ value, onChange, disabled }: ManufacturerPickerProps) {
	const trpc = useTRPC();

	const { data: manufacturersData } = useQuery(trpc.manufacturer.getMany.queryOptions({}));
	const manufacturers = manufacturersData?.manufacturers;

	return (
		<Autocomplete
			loading={!manufacturers}
			disabled={disabled}
			autoHighlight
			autoSelect
			disableClearable
			handleHomeEndKeys
			openOnFocus
			options={manufacturers ?? [{ id: '', name: '' }]}
			value={
				typeof value === 'object' ?
					{ id: 'create', name: value.create }
				:	(manufacturers?.find((manufacturer) => value === manufacturer.id) ?? { id: '', name: '' })
			}
			onChange={(event, newValue) => {
				if (newValue.id === 'create') {
					onChange({ create: newValue.name });
				} else {
					onChange(newValue.id);
				}
			}}
			renderInput={({ InputProps, ...params }) => (
				<TextField
					{...params}
					label='Manufacturer'
					slotProps={{
						input: {
							...InputProps,
							endAdornment:
								typeof value === 'object' ?
									<Tooltip title='This will be created on submit.'>
										<InfoOutlinedIcon className='cursor-initial' />
									</Tooltip>
								:	undefined,
						},
					}}
				/>
			)}
			getOptionLabel={(option) => option.name}
			renderOption={({ key, ...props }, manufacturer) => (
				<li key={manufacturer.id} {...props}>
					<p>{manufacturer.id === 'create' ? `Create "${manufacturer.name}"` : manufacturer.name}</p>
				</li>
			)}
			filterOptions={(options, params) => {
				const filtered = filter(options, params);

				// Add option to create a new manufacturer if the input value is not in the list
				const isInputInOptions =
					params.inputValue === '' ||
					filtered.some((option) => option.name.toLocaleLowerCase() === params.inputValue.toLocaleLowerCase());
				if (!isInputInOptions) {
					filtered.push({ id: 'create', name: params.inputValue });
				}

				return filtered;
			}}
		/>
	);
}
